/**
 * appHeader.js
 *
 * Self-contained replacement for boot.js's:
 *   - nav-bar / createNavHtmlforPages() / menu-btn toggle logic
 *   - loadLangDropdown() / opendropdownDiv() / closedropdownDiv()
 *   - showOverlay() / hideOverlay()
 *
 * Usage — in any page's main JS file:
 *   import { mountAppHeader } from './header/appHeader.js'
 *   const { syncAvatar } = mountAppHeader()
 *
 * IMPORTANT — avatar sync:
 *   authenticateCurUser() (in authSession-adjacent code) sets the popover's
 *   avatar via the ".user-profile-heading-img" selector, same as before.
 *   Call the returned `syncAvatar()` right after authenticateCurUser resolves
 *   so the small trigger-button avatar mirrors whatever the popover now shows —
 *   otherwise the two can drift out of sync (this was a latent bug in the
 *   original design too, just hidden because both were built from the same
 *   static markup at the same time).
 *
 *   await authenticateCurUser({ ... })
 *   syncAvatar()
 *
 * This also sets document.body padding-top equal to the header's height, so
 * the header reserves its own space instead of overlapping page content.
 */
import './appHeader.css';
import { headerTheme } from './headerTheme.js';
import { createPopover } from './popover.js';
import { currLang } from '../translation.js';
import { homePageImages, iconIndexMap } from '../img_sound_imports.js';
import { logoutSession, redirectToFreshPortalLogin, getAccessToken } from '../auth/authSession.js';
import { emptyLevels } from '../boot.js';
import { orgMainPortalUrl } from '../orgContext.js';

const ICON_HAMBURGER = `<i data-lucide="menu"></i>`;
const ICON_USER = `<i data-lucide="user"></i>`;
const ICON_WORLD = `<i data-lucide="globe"></i>`;
const ICON_CHEVRON_DOWN = `<i data-lucide="chevron-down"></i>`;
const ICON_REFRESH = `<i data-lucide="refresh-cw"></i>`;
const ICON_LOGOUT = `<i data-lucide="log-out"></i>`;

const HEADER_HEIGHT_PX = 70;

const LEVEL_PAGES = [
  'level_home',
  'dragndrop',
  'sequence',
  'loops',
  'loops2',
  'events',
  'conditionals',
  'nested_loops',
  'functions',
  'variables',
  'data_Simulation',
];

export const dropdownLanguages = [
  ['English', 'eng'],
  ['हिंदी', 'hin'],
  ['ગુજરાતી', 'gu'],
  ['ಕನ್ನಡ', 'kn'],
  ['मराठी', 'mr'],
  ['ਪੰਜਾਬੀ', 'pa'],
  ['தமிழ்', 'tam'],
  ['اردو', 'ur'],
  ['عربي', 'ar'],
  ['Czech', 'cs'],
  ['Filipno', 'tl'],
  ['Swahili', 'sw'],
  ['नेपाली', 'ne'],
  ['తెలుగు', 'te'],
];

function buildNavPagesArr(page) {
  const navigatingUrl = orgMainPortalUrl('/#/home/student-courses');
  let pagesArr = [
    ['Back', 'home', 26],
    [currLang['Home.LabelHome'], 'home', 2],
  ];
  if (navigatingUrl) {
    pagesArr.push(['LXP', navigatingUrl, 25]);
  }
  let pageHeading;

  if (
    ['factory.html', 'web_factory.html', 'python_factory.html', 'js_factory.html'].includes(page)
  ) {
    pagesArr[0][1] = 'build_practice';

    const factoryHeadings = {
      'factory.html': currLang['Home.labelPractice2'],
      'web_factory.html': `${currLang['Home.labelWeb2']}  ${currLang['Home.labelPractice2']}`,
      'python_factory.html': `${currLang['Home.labelPython']} ${currLang['Home.labelPractice2']}`,
      'js_factory.html': `JS ${currLang['Home.labelPractice2']}`,
    };
    pageHeading = factoryHeadings[page];

    pagesArr.push(
      [currLang['Home.labelPractice2'], 'factory', 17, currLang['Home.labelPractice2']],
      [
        `${currLang['Home.labelWeb2']}  ${currLang['Home.labelPractice2']}`,
        'web_factory',
        18,
        `${currLang['Home.labelWeb2']}  ${currLang['Home.labelPractice2']}`,
      ],
      [
        `${currLang['Home.labelPython']} ${currLang['Home.labelPractice2']}`,
        'python_factory',
        19,
        `${currLang['Home.labelPython']} ${currLang['Home.labelPractice2']}`,
      ],
      [
        `JS ${currLang['Home.labelPractice2']}`,
        'js_factory',
        20,
        `JS ${currLang['Home.labelPractice2']}`,
      ]
    );
  } else if (page.includes('music_factory') || page.includes('art_factory')) {
    pagesArr[0][1] = 'musicxart';
    pageHeading = page.includes('music_factory') ? 'Music' : 'Art';
    pagesArr.push(['Art', 'art_factory', 22], ['Music', 'music_factory', 23]);
  } else if (page.includes('vernatics')) {
    pagesArr[0][1] = 'transitionxart';
    pageHeading = currLang['TransitionPage.Vernatics'];
  } else if (page.includes('compiler') || page.includes('sketch')) {
    pagesArr[0][1] = 'transformation';

    const compilerHeadings = {
      sketch: 'p5js',
      pythoncompiler: currLang['Home.labelPython'] + ' ' + currLang['Compiler.Text'],
      jscompiler: 'JS ' + currLang['Compiler.Text'],
    };
    const matchedCompilerKey = Object.keys(compilerHeadings).find((key) => page.includes(key));
    if (matchedCompilerKey) pageHeading = compilerHeadings[matchedCompilerKey];

    pagesArr.push(
      ['P5Js', 'sketch', 24, 'p5js'],
      [
        'Python Compiler',
        'pythoncompiler',
        19,
        currLang['Home.labelPython'] + ' ' + currLang['Compiler.Text'],
      ],
      ['JS Compiler', 'jscompiler', 20, 'JS ' + currLang['Compiler.Text']]
    );
  } else if (page === 'home.html' || page === '') {
    // Landing page — only the LXP link, no Back/Home (we're already home)
    pagesArr = [['LXP', navigatingUrl, 25]];
  } else if (page === 'level_home.html') {
    // Levels index — just Home + LXP, not the full per-level list below
    pagesArr = [
      [currLang['Home.LabelHome'], 'home', 2],
      ['LXP', navigatingUrl, 25],
    ];
  } else if (
    page.includes('index') ||
    page.includes('transitionxart') ||
    page.includes('activities')
  ) {
    pagesArr[0][1] = 'home';
    pagesArr.shift();
  } else if (
    page.includes('build_practice') ||
    page === 'vernatics.html' ||
    page.includes('vernatics') ||
    page.includes('musicxart') ||
    page.includes('transformation')
  ) {
    pagesArr[0][1] = 'transitionxart';
  } else {
    pagesArr[0][1] = 'level_home';

    const levelHeadings = {
      dragndrop: currLang['Home.LevelLabel1'],
      sequence: currLang['Home.LevelLabel2'],
      loops2: currLang['Home.LevelLabel4'],
      loops: currLang['Home.LevelLabel3'],
      events: currLang['Home.LevelLabel5'],
      conditionals: currLang['Home.LevelLabel6'],
      nested_loops: currLang['Home.LevelLabel7'],
      functions: currLang['Home.LevelLabel8'],
      variables: currLang['Home.LevelLabel9'],
      data_Simulation: currLang['Home.LevelLabel10'],
    };
    const matchedLevelKey = Object.keys(levelHeadings).find((key) => page.includes(key));
    if (matchedLevelKey) pageHeading = levelHeadings[matchedLevelKey];

    pagesArr.push(
      [currLang['Home.LevelLabel1'], 'dragndrop', 3],
      [currLang['Home.LevelLabel2'], 'sequence', 4],
      [currLang['Home.LevelLabel3'], 'loops', 5],
      [currLang['Home.LevelLabel4'], 'loops2', 6],
      [currLang['Home.LevelLabel5'], 'events', 7],
      [currLang['Home.LevelLabel6'], 'conditionals', 8],
      [currLang['Home.LevelLabel7'], 'nested_loops', 9],
      [currLang['Home.LevelLabel8'], 'functions', 10],
      [currLang['Home.LevelLabel9'], 'variables', 11],
      [currLang['Home.LevelLabel10'], 'data_Simulation', 12]
    );
  }
  return { pagesArr, pageHeading };
}

function applyThemeVars(rootEl) {
  Object.entries({
    '--header-height': headerTheme.headerHeight,
    '--profile-gradient-start': headerTheme.profilePopoverGradientStart,
    '--profile-gradient-end': headerTheme.profilePopoverGradientEnd,
    '--profile-border': headerTheme.profilePopoverBorder,
    '--profile-accent': headerTheme.profilePopoverAccent,
    '--profile-muted-bg': headerTheme.profilePopoverMutedBg,
    '--danger-outline-color': headerTheme.dangerOutlineColor,
    '--danger-outline-border': headerTheme.dangerOutlineBorder,
    '--danger-outline-hover-bg': headerTheme.dangerOutlineHoverBg,
    '--danger-shadow-color': headerTheme.dangerShadowColor,
    '--nav-icon-color': headerTheme.navIconColor,
    '--nav-icon-chip-bg': headerTheme.navIconChipBg,
    '--header-bg': headerTheme.headerBg,
    '--header-border': headerTheme.headerBorder,
    '--header-title-color': headerTheme.headerTitleColor,
    '--trigger-bg': headerTheme.triggerBg,
    '--trigger-bg-hover': headerTheme.triggerBgHover,
    '--trigger-icon-color': headerTheme.triggerIconColor,
    '--popover-bg': headerTheme.popoverBg,
    '--popover-border': headerTheme.popoverBorder,
    '--popover-shadow': headerTheme.popoverShadow,
    '--popover-text': headerTheme.popoverText,
    '--popover-text-muted': headerTheme.popoverTextMuted,
    '--popover-divider': headerTheme.popoverDivider,
    '--popover-hover-bg': headerTheme.popoverHoverBg,
    '--danger-bg': headerTheme.dangerBg,
    '--danger-bg-hover': headerTheme.dangerBgHover,
    '--danger-text': headerTheme.dangerText,
    '--avatar-ring': headerTheme.avatarRing,
    '--radius-sm': headerTheme.radiusSm,
    '--radius-md': headerTheme.radiusMd,
    '--radius-full': headerTheme.radiusFull,
  }).forEach(([k, v]) => rootEl.style.setProperty(k, v));
}

function renderClearDataConfirm({ onConfirm }) {
  const overlay = document.createElement('div');
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '99999',
  });
  overlay.innerHTML = `
    <div style="background:#fff9f0;border:3px solid #ff6b6b;border-radius:20px;padding:32px 28px 24px;max-width:340px;width:90%;text-align:center;font-family:Inter,Arial,sans-serif;box-shadow:0 8px 32px rgba(0,0,0,0.25);">
      <div style="font-size:40px;">⚠️</div>
      <h3 style="margin:12px 0 8px;color:#cc2200;font-size:18px;font-weight:700;">Reset Progress?</h3>
      <p style="color:#444;font-size:14px;margin:0 0 24px;line-height:1.5;">${currLang['ClearData.Confirm'] ?? 'This will reset all your level progress. This cannot be undone.'}</p>
      <div style="display:flex;gap:12px;justify-content:center;">
        <button class="cd-cancel" style="padding:10px 24px;border-radius:30px;border:2px solid #ccc;background:#fff;color:#555;font-weight:600;font-size:14px;cursor:pointer;">Cancel</button>
        <button class="cd-confirm" style="padding:10px 24px;border-radius:30px;border:none;background:linear-gradient(135deg,#ff6b6b,#cc2200);color:#fff;font-weight:700;font-size:14px;cursor:pointer;">Yes, Reset</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  overlay.querySelector('.cd-cancel').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  overlay.querySelector('.cd-confirm').addEventListener('click', () => {
    overlay.remove();
    onConfirm();
  });
}

function showToast(message) {
  const toast = document.createElement('div');
  toast.textContent = message;
  Object.assign(toast.style, {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#1a1a22',
    color: '#fff',
    padding: '12px 20px',
    borderRadius: '12px',
    fontSize: '14px',
    zIndex: '99999',
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
  });
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 2500);
}

/**
 * Builds an avatar slot: a real, permanent <img> (so authenticateCurUser's
 * direct .src assignment always works) layered under a fallback icon overlay.
 * The overlay auto-hides once the <img> successfully loads a real source,
 * and re-shows if the <img> has no src or errors.
 */
function buildAvatarSlot({ imgClass, wrapClass }) {
  const wrap = document.createElement('span');
  wrap.className = wrapClass;

  const img = document.createElement('img');
  img.className = `${imgClass} user-profile-heading-img`;
  img.alt = '';

  const skeleton = document.createElement('span');
  skeleton.className = 'avatar-skeleton';

  const fallback = document.createElement('span');
  fallback.className = `${imgClass}-fallback avatar-fallback-icon`;
  fallback.innerHTML = ICON_USER;
  fallback.style.display = 'none';

  let settled = false; // becomes true once we know success/failure — stops showing skeleton forever

  const handleLoad = () => {
    settled = true;
    skeleton.style.display = 'none';
    fallback.style.display = 'none';
  };

  const handleError = () => {
    settled = true;
    skeleton.style.display = 'none';
    fallback.style.display = 'flex';
  };

  img.addEventListener('load', handleLoad);
  img.addEventListener('error', handleError);

  new MutationObserver(() => {
    const hasRealSrc = img.getAttribute('src');
    if (!hasRealSrc && !settled) {
      skeleton.style.display = 'block';
      fallback.style.display = 'none';
    }
  }).observe(img, { attributes: true, attributeFilter: ['src', 'class'] });

  wrap.append(img, skeleton, fallback);
  return { wrap, img, fallback, skeleton };
}

export function mountAppHeader({ avatarUrl = '' } = {}) {
  const page = window.location.pathname.split('/').pop();
  const { pagesArr, pageHeading: detectedHeading } = buildNavPagesArr(page);

  // ── Header bar ─────────────────────────────────────────────
  const header = document.createElement('header');
  header.className = 'app-header';
  applyThemeVars(header);

  const hamburgerBtn = document.createElement('button');
  hamburgerBtn.className = 'app-header__trigger';
  hamburgerBtn.setAttribute('aria-label', 'Menu');
  hamburgerBtn.innerHTML = ICON_HAMBURGER;

  const brand = document.createElement('div');
  brand.className = 'app-header__brand';

  brand.innerHTML = `
  <img src="${homePageImages.activity}" class="app-header__logo" alt="AptCoder">
  <span class="app-header__title">${detectedHeading ?? ''}</span>
`;

  // Header trigger gets its OWN avatar slot (separate <img> from the popover's).
  // authenticateCurUser only ever targets ".user-profile-heading-img" — which
  // by querySelector grabs the FIRST match in the DOM. Since the popover's
  // slot is appended to the DOM after this one in mountAppHeader, this header
  // <img> would normally be the one authenticateCurUser actually updates.
  // syncAvatar() below keeps the popover's slot mirrored to this one.
  const isInIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();
  const avatarBtn = document.createElement('button');
  avatarBtn.className = 'app-header__avatar-btn profile-btn';
  avatarBtn.setAttribute('aria-label', 'Profile menu');
  // avatarBtn.style.display = 'none'; // authenticateCurUser flips this to 'block' once logged in confirmed
  const headerAvatar = buildAvatarSlot({
    imgClass: 'app-header__avatar',
    wrapClass: 'app-header__avatar-render',
  });
  if (avatarUrl) headerAvatar.img.src = avatarUrl;
  avatarBtn.appendChild(headerAvatar.wrap);
  header.append(hamburgerBtn, brand, avatarBtn);
  document.body.appendChild(header);

  // ── Nav popover ────────────────────────────────────────────
  const navPanel = document.createElement('div');
  navPanel.className = 'popover-panel popover-panel--nav';
  navPanel.setAttribute('aria-hidden', 'true');
  applyThemeVars(navPanel);

  const navList = document.createElement('ul');
  navList.className = 'popover-nav-list';
  pagesArr.forEach(([label, target, iconId]) => {
    if (window.location.pathname === '/' + target + '.html') return;
    const li = document.createElement('li');
    const href = target.startsWith('http') ? target : `${target}.html`;
    const iconSrc = iconIndexMap[iconId];
    li.innerHTML = `<a href="${href}">${
      iconSrc
        ? `<span class="nav-icon-chip"><img class="nav-icon" src="${iconSrc}" alt="" onerror="this.style.display='none'" /></span>`
        : ''
    }<span>${label}</span></a>`;
    navList.appendChild(li);
  });
  navPanel.appendChild(navList);
  document.body.appendChild(navPanel);
  const navPopover = createPopover({
    trigger: hamburgerBtn,
    panel: navPanel,
    placement: 'bottom-start',
  });

  // ── Profile popover ──────────────────────────────────────
  const profilePanel = document.createElement('div');
  profilePanel.className = 'popover-panel popover-panel--profile';
  profilePanel.setAttribute('aria-hidden', 'true');
  applyThemeVars(profilePanel);

  const isLevelPage = LEVEL_PAGES.some((p) => page.includes(p));
  const isLoggedIn = Boolean(getAccessToken());

  profilePanel.innerHTML = `
    <div class="profile-popover__header">
      <div class="profile-popover__avatar-slot"></div>
      <p class="profile-popover__name username-display" style="display:none;"></p>
      <p class="profile-popover__subtitle"></p>
    </div>
    <div class="profile-popover__row">
      <label class="profile-popover__label">${currLang['Language.Text'] ?? 'Preferred language'}</label>
      <div class="profile-popover__select-wrap">
        <span class="profile-popover__select-icon">${ICON_WORLD}</span>
        <select class="profile-popover__select" id="select-language"></select>
        <span class="profile-popover__select-chevron">${ICON_CHEVRON_DOWN}</span>
      </div>
    </div>
    ${isLevelPage ? `<button class="profile-popover__btn profile-popover__btn--muted" id="clear-data-btn">${ICON_REFRESH}<span>${currLang['ClearData.Text']}</span></button>` : ''}
    ${isLoggedIn ? `<button class="profile-popover__btn profile-popover__btn--danger logout-btn" id="logout-btn">${ICON_LOGOUT}<span>${currLang['LogoutButton.Text']}</span></button>` : ''}
  `;
  document.body.appendChild(profilePanel);

  // Popover's avatar slot — own <img>, kept visually mirrored to the header's
  // via syncAvatar(), since authenticateCurUser only updates the FIRST
  // ".user-profile-heading-img" match (the header one) in the DOM.
  const panelAvatarSlot = profilePanel.querySelector('.profile-popover__avatar-slot');
  const panelAvatar = buildAvatarSlot({
    imgClass: 'profile-popover__avatar',
    wrapClass: 'profile-popover__avatar-wrap',
  });
  if (avatarUrl) panelAvatar.img.src = avatarUrl;
  panelAvatarSlot.appendChild(panelAvatar.wrap);
  // Online status dot — purely decorative, matches reference design
  const statusDot = document.createElement('span');
  statusDot.className = 'profile-popover__status-dot';
  panelAvatarSlot.appendChild(statusDot);

  const profilePopover = createPopover({
    trigger: avatarBtn,
    panel: profilePanel,
    placement: 'bottom-end',
  });

  // ── Language select ────────────────────────────────────────
  const select = profilePanel.querySelector('#select-language');
  const savedLang = localStorage.getItem('language');
  dropdownLanguages.forEach(([label, code]) => {
    const opt = document.createElement('option');
    opt.value = code;
    opt.textContent = label;
    if (savedLang?.includes(code)) opt.selected = true;
    select.appendChild(opt);
  });
  select.addEventListener('change', () => {
    localStorage.setItem('language', JSON.stringify([[select.value]]));
    location.reload();
  });

  profilePanel.querySelector('#logout-btn')?.addEventListener('click', async () => {
    await logoutSession();
    redirectToFreshPortalLogin();
  });

  profilePanel.querySelector('#clear-data-btn')?.addEventListener('click', () => {
    profilePopover.close();
    renderClearDataConfirm({
      onConfirm: () => {
        emptyLevels();
        showToast('✅ Your data has been successfully cleared.');
        setTimeout(() => {
          window.location.href = 'level_home.html';
        }, 2500);
      },
    });
  });

  /**
   * syncAvatar — mirrors the header avatar's current src/state onto the
   * popover avatar (and vice versa is unnecessary since authenticateCurUser
   * always hits the header's <img> first via querySelector).
   * Exposed on window so authenticateCurUser can call it directly.
   */
  function syncAvatar() {
    const src = headerAvatar.img.getAttribute('src');
    if (src) panelAvatar.img.src = src;
  }
  window.__syncHeaderAvatar = syncAvatar;

  return { navPopover, profilePopover, headerEl: header, syncAvatar };
}
